if ( typeof PickupAvailabilityLive !== 'function' ) {

  // Shopify sunsets API versions ~1 year after release; bump this periodically.
  const STOREFRONT_API_VERSION = '2026-07';

  class PickupAvailabilityLive extends HTMLElement {

    connectedCallback() {
      this.locationElements = [...this.querySelectorAll('[data-location-id]')];
      if ( ! this.locationElements.length ) return;
      // Localized via Liquid `| t` and passed in as data attributes (see
      // pickup-availability-widget.liquid) rather than hardcoded here, so
      // this JS never bakes in English-only text.
      this.textAvailableNow = this.dataset.textAvailableNow;
      this.textContactStore = this.dataset.textContactStore;
      this.abortController = new AbortController();
      this.fetchLiveAvailability();
    }

    disconnectedCallback() {
      // A variant switch (or navigating away from a quick view) destroys and
      // recreates this element via innerHTML replacement without waiting for
      // any in-flight request. Without this, a slow response for the old
      // variant could resolve after the new variant's element already exists
      // and — since sidebar elements are matched globally by variant id, not
      // scoped to a specific element instance — patch the new variant's
      // sidebar with the old variant's stale quantities.
      this.abortController?.abort();
    }

    // The "check other stores" sidebar is cloned from a <template> into
    // document.body by a separate custom element (PickupAvailabilityWidget,
    // in component-product-form.js), so it lives outside this element's
    // subtree. Its wrapper id isn't unique across multiple quick-view opens
    // on one page (product-quick-view.liquid reuses the same section.id for
    // every product), so rather than trust getElementById on that id,
    // sidebar elements carry their own data-variant-id and get matched
    // against this instance's variant — variant ids are globally unique, so
    // this is safe both across multiple quick views for different products
    // and across a variant switch on the same product.
    getAllLocationElements() {
      const variantId = this.dataset.variantId;
      const sidebarElements = variantId
        ? [...document.querySelectorAll(`[data-location-id][data-variant-id="${variantId}"]`)]
        : [];
      return [...this.locationElements, ...sidebarElements];
    }

    getAllDotElements() {
      const variantId = this.dataset.variantId;
      return variantId
        ? [...document.querySelectorAll(`[data-dot-location-id][data-variant-id="${variantId}"]`)]
        : [];
    }

    setText(el, text) {
      (el.querySelector('strong') || el).textContent = text;
    }

    setDotWarning(el) {
      el.classList.remove('alert--success');
      el.classList.add('alert--warning');
    }

    applyFallbackToAll() {
      this.getAllLocationElements().forEach(el => this.setText(el, this.textContactStore));
      this.getAllDotElements().forEach(el => this.setDotWarning(el));
    }

    async fetchLiveAvailability() {

      const token = this.dataset.token;
      const shopDomain = this.dataset.shopDomain;

      if ( ! token || ! shopDomain ) {
        this.applyFallbackToAll();
        return;
      }

      try {

        const response = await fetch(`https://${shopDomain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
          method: 'POST',
          signal: this.abortController.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({
            query: `query PickupAvailability($id: ID!) {
              node(id: $id) {
                ... on ProductVariant {
                  storeAvailability(first: 10) {
                    edges { node { quantityAvailable location { id } } }
                  }
                }
              }
            }`,
            variables: { id: `gid://shopify/ProductVariant/${this.dataset.variantId}` }
          })
        });

        if ( ! response.ok ) throw new Error(`Storefront API responded ${response.status}`);

        const { data, errors } = await response.json();
        if ( errors && errors.length ) throw new Error(errors[0].message);

        // Belt-and-suspenders alongside the abort signal: even if a response
        // raced past the abort, never mutate DOM for an instance that's
        // already been replaced by a variant switch.
        if ( ! this.isConnected ) return;

        const edges = data?.node?.storeAvailability?.edges || [];
        const quantityByLocationId = {};
        edges.forEach(({ node }) => {
          const locationId = node.location.id.split('/').pop();
          quantityByLocationId[locationId] = node.quantityAvailable;
        });

        this.getAllLocationElements().forEach(el => {
          const locationId = el.dataset.locationId;
          if ( ! (locationId in quantityByLocationId) ) {
            this.setText(el, this.textContactStore);
            return;
          }
          if ( quantityByLocationId[locationId] >= 1 ) {
            this.setText(el, this.textAvailableNow);
          }
          // else: leave the server-rendered pick_up_time text as-is
        });

        this.getAllDotElements().forEach(el => {
          const locationId = el.dataset.dotLocationId;
          const quantity = quantityByLocationId[locationId];
          // green (alert--success, server-rendered default) only when confirmed in stock;
          // 0 in stock or unknown (missing from response) both read as not-immediately-available
          if ( ! (quantity >= 1) ) this.setDotWarning(el);
        });

      } catch (error) {
        if ( error.name === 'AbortError' ) return; // expected: superseded by a variant switch
        if ( ! this.isConnected ) return;
        console.error('pickup-availability-live:', error);
        this.applyFallbackToAll();
      }

    }

  }

  if ( typeof customElements.get('pickup-availability-live') == 'undefined' ) {
    customElements.define('pickup-availability-live', PickupAvailabilityLive);
  }

}
